import { useEffect, useRef, useState } from 'react';
import App from './App';
import { AccessGate } from './AccessGate';
import { resolveZoomAccess, type ZoomAccess } from './zoomAccess';
import { shouldShowZoomDiagnostics } from './zoomSdk';

const SLOW_MS = 4_000;
const HARD_TIMEOUT_MS = 35_000;

export function Bootstrap() {
  const [access, setAccess] = useState<ZoomAccess>({ status: 'loading' });
  const [slow, setSlow] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    const slowTimer = window.setTimeout(() => setSlow(true), SLOW_MS);
    const hardTimer = window.setTimeout(() => {
      setAccess((prev) =>
        prev.status === 'loading'
          ? {
              status: 'blocked',
              inZoom: shouldShowZoomDiagnostics(),
              reason: 'timeout',
              detail: 'Startup timed out waiting for Zoom SDK',
            }
          : prev,
      );
    }, HARD_TIMEOUT_MS);

    void resolveZoomAccess()
      .then((result) => {
        if (!cancelled) setAccess(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setAccess({
            status: 'blocked',
            inZoom: shouldShowZoomDiagnostics(),
            reason: 'sdk-error',
            detail: err instanceof Error ? err.message : 'boot failed',
          });
        }
      })
      .finally(() => {
        window.clearTimeout(slowTimer);
        window.clearTimeout(hardTimer);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(slowTimer);
      window.clearTimeout(hardTimer);
    };
  }, []);

  if (access.status === 'ready') {
    return <App zoom={access} />;
  }

  if (access.status === 'loading') {
    return <p className="access-hint">{slow ? 'ZCS:wait' : 'ZCS:…'}</p>;
  }

  return <AccessGate access={access} />;
}
